import { useState, useEffect, useContext } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import {
	deserializeState,
	useGetOnboardingState,
	useSetOnboardingState,
} from '@confluence/onboarding-helpers/entry-points/hooks/useOnboardingState';
import { ONBOARDING_PRESENCE_PAGE_STATE_KEYS } from '@confluence/onboarding-helpers/entry-points/constants/onboarding-state-constants';
import { SPAViewContext } from '@confluence/spa-view-context';
import { useIsProductAdmin } from '@confluence/current-user';
import { useAccessStatus } from '@confluence/session-data';
import { fg } from '@confluence/feature-gating';

import {
	type PresenceChangeboardingFeatureResponse,
	type PresenceChangeboardingFeatureProps,
	ChangeboardingState,
} from './PresenceChangeboarding';
import image from './assets/images/telepointer-spotlight-img.png';

type UseTelepointerChangeboarding = (
	args: PresenceChangeboardingFeatureProps,
) => PresenceChangeboardingFeatureResponse;

const i18n = defineMessages({
	heading: {
		id: 'team-presence.avatar-group.spotlight.telepointer.title',
		defaultMessage: 'Find where people are editing',
		description:
			'Title for a changeboarding spotlight that appears when user first enters a page to teach them how to locate another user on the page by clicking the avatar or presence menu item.',
	},
	description: {
		id: 'team-presence.avatar-group.spotlight.telepointer.description',
		defaultMessage: 'Click an editor’s avatar to jump to where they’re working in the page or doc.',
		description:
			'Description for a changeboarding spotlight shown to users that appears when user first enters a page to teach them how to locate another user on the page by clicking the avatar or presence menu item.',
	},
	primaryAction: {
		id: 'team-presence.avatar-group.spotlight.telepointer.primary-action',
		defaultMessage: 'OK',
		description:
			'Button text for a primary action on a changeboarding spotlight to acknowledge that you read the spotlight',
	},
	accessibleLabel: {
		id: 'team-presence.avatar-group.spotlight.telepointer.label',
		defaultMessage: 'Onboarding for click to find users feature',
		description:
			'Accessible label for a changeboarding spotlight that appears when user first enters a page to teach them how to locate another user on the page by clicking the avatar or presence menu item.',
	},
});

const FEATURE_NAME = 'telepointer';
// TODO: This must be the dynamic analytics source
// https://product-fabric.atlassian.net/browse/MDS-1330
export const TELEPOINTER_AVATAR_GROUP_ANALYTIC_SOURCE = 'telepointerAvatarGroup';

export const useTelepointerChangeboarding: UseTelepointerChangeboarding = ({
	contentId,
	contentMode,
	contentType,
	totalParticipants,
	skip,
}) => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [state, setState] = useState<ChangeboardingState>(ChangeboardingState.INITIAL);
	const { setOnboardingState, error: setError } = useSetOnboardingState();
	const {
		data: getData,
		error: getError,
		loading: getLoading,
	} = useGetOnboardingState(
		ONBOARDING_PRESENCE_PAGE_STATE_KEYS.TELEPOINTER_AVATAR_GROUP_PAGE_SPOTLIGHT_SEEN,
	);
	const { pageTelepointerAvatarGroupSpotlightSeen: isSeen } = deserializeState(getData);
	const { isProductAdmin } = useIsProductAdmin();
	const {
		isNewUser,
		loading: SPAViewContextLoading,
		error: SPAViewContextError,
	} = useContext(SPAViewContext);
	const { isLicensed, isGuest } = useAccessStatus();
	const hasMoreThanOneParticipant = Boolean(totalParticipants && totalParticipants > 1);

	useEffect(() => {
		if (
			skip ||
			getLoading ||
			SPAViewContextLoading ||
			!fg('confluence_team_presence_scroll_to_pointer_cb')
		) {
			return;
		}

		if (setError || getError || SPAViewContextError) {
			setState(ChangeboardingState.ERROR);
			return;
		}

		if (isSeen) {
			setState(ChangeboardingState.COMPLETED);
			return;
		}

		if (
			// This feature is only available for live docs, pages and blogposts in edit mode
			(((contentType === 'page' || contentType === 'blogpost') && contentMode === 'edit') ||
				contentMode === 'live') &&
			!isSeen &&
			!isNewUser &&
			(isLicensed || isGuest) &&
			(isProductAdmin || hasMoreThanOneParticipant)
		) {
			setState(ChangeboardingState.READY);
			return;
		}
	}, [
		contentMode,
		contentType,
		getError,
		getLoading,
		hasMoreThanOneParticipant,
		isGuest,
		isLicensed,
		isNewUser,
		isProductAdmin,
		isSeen,
		setError,
		skip,
		SPAViewContextLoading,
		SPAViewContextError,
	]);

	useEffect(() => {
		if (state === ChangeboardingState.ERROR) {
			// TODO: This should be a subexperience errpr
			createAnalyticsEvent({
				type: 'sendTrackEvent',
				data: {
					source: TELEPOINTER_AVATAR_GROUP_ANALYTIC_SOURCE,
					actionSubject: 'PresenceAvatarGroup',
					action: 'pulse failed',
					attributes: {
						contentId,
						contentMode,
						contentType,
						changeboardingFeature: FEATURE_NAME,
					},
				},
			}).fire();
		}
	}, [contentId, contentType, contentMode, state, createAnalyticsEvent]);

	const handleComplete = () => {
		setState(ChangeboardingState.COMPLETED);
		void setOnboardingState({
			key: ONBOARDING_PRESENCE_PAGE_STATE_KEYS.TELEPOINTER_AVATAR_GROUP_PAGE_SPOTLIGHT_SEEN,
			value: 'true',
		});
	};

	const handleClose = () => {
		handleComplete();
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				source: `${TELEPOINTER_AVATAR_GROUP_ANALYTIC_SOURCE}Spotlight`,
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'telepointerSpotlightClose',
			},
		}).fire();
	};

	const handlePulseClick = () => {
		setState(ChangeboardingState.ACTIVE);
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				source: `${TELEPOINTER_AVATAR_GROUP_ANALYTIC_SOURCE}Spotlight`,
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'telepointerPulse',
			},
		}).fire();
	};

	const heading = formatMessage(i18n.heading);
	const label = formatMessage(i18n.accessibleLabel);
	const description = formatMessage(i18n.description);
	const actions = [
		{
			onClick: handleClose,
			text: formatMessage(i18n.primaryAction),
		},
	];

	return {
		featureName: FEATURE_NAME,
		state,
		content: {
			image,
			actions,
			heading,
			label,
			description,
			onPulseClick: handlePulseClick,
			onBlanketClick: handleClose,
		},
	};
};
